
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Form } from "element-ui";
import { Moment } from "moment";
import { getCustomerAllInfo } from "@/api/customer";
import { creatShipping, shippingOrderCodePreview } from "@/api/shipping-order";
import { IContainerTypesModel, IUserModel } from "@/api/types";
import { queryMarkLetter } from "@/api/purchase-order";
import SingleSelect from "@/views/customs-management/edit/singleSelect.vue";
import UpdateMark, { Mark } from "@/views/customs-management/edit/mark.vue";
import SelectCustomerDialog from "@/views/customs-management/component/SelectCustomerDialog.vue";

interface SelectedCustomer {
  id: string;
  code: string;
  abbreviation: string;
}
@Component({
  name: "CreateShippingOrderDialog",
  components: {
    SingleSelect,
    UpdateMark,
    SelectCustomerDialog,
  },
})
export default class extends Vue {
  @Prop({ default: false }) private visible!: boolean;
  @Prop({ default: () => [] }) private sellerList!: IUserModel[];

  private dialogVisible = false;
  private showSelectCustomer = false;
  private showMarkEdit = false;
  private submitting = false;
  private previewLoading = false;
  private markInput: string[] = [];
  private markLetters: Mark[] = [];
  private shippingCodePrefix = "";
  private shippingCodeSuffix = "";
  private payload = new WriteShippingOrderInput();

  private internationalTradeTermsOptions = [
    { label: "FOB", value: "1" },
    { label: "CIF", value: "2" },
    { label: "CNF", value: "3" },
    { label: "DPU", value: "4" },
  ];

  private paymentOptions = [
    { label: "定金", value: "1" },
    { label: "赊账付款(OA)", value: "2" },
    { label: "信用证(L/C)", value: "3" },
    { label: "付款交单(DP)", value: "4" },
    { label: "发货前付款", value: "5" },
    { label: "见提单付款", value: "6" },
    { label: "其它", value: "7" },
  ];

  private rules = {
    shippingMark: [
      { required: true, message: "请选择发货唛头", trigger: "change" },
    ],
    name: [{ required: true, message: "请输入BL公司名", trigger: "change" }],
    address: [
      { required: true, message: "请输入BL公司地址", trigger: "change" },
    ],
    departures: [
      { required: true, message: "请输入出口港", trigger: "change" },
    ],
    tradeCountry: [
      { required: true, message: "请输入贸易国", trigger: "blur" },
    ],
    departure: [
      { required: true, message: "请输入出境关别", trigger: "change" },
    ],
    transportation: [
      { required: true, message: "请输入运输方式", trigger: "change" },
    ],
    destinationCountry: [
      { required: true, message: "请输入最终目的国", trigger: "change" },
    ],
    deliveryCountry: [
      { required: true, message: "请输入运抵国", trigger: "change" },
    ],
    internationalTradeTerms: [
      { required: true, message: "请选择成交方式", trigger: "change" },
    ],
    portDischarge: [
      { required: true, message: "请输入指运港", trigger: "change" },
    ],
    ladingType: [
      { required: true, message: "请输入提单类型", trigger: "change" },
    ],
    payment: [{ required: true, message: "请选择付款方式", trigger: "change" }],
    paymentDetails: [
      {
        required: true,
        message: "请输入报关销售合同付款方式",
        trigger: "blur",
      },
    ],
    notifier: [
      { required: true, message: "请输入BL通知人公司名", trigger: "change" },
    ],
    notifierAddress: [
      { required: true, message: "请输入BL通知人地址", trigger: "change" },
    ],
  };

  get customerTag() {
    if (!this.payload.code) return "";
    return `${this.payload.code} ${this.payload.abbreviation || ""}`.trim();
  }

  get sellerDisplay() {
    if (!this.payload.seller) return "";
    const seller = this.sellerList.find(
      (item) => item.id === this.payload.seller
    );
    if (!seller) {
      // 客人接口可能直接返回销售员名称字符串
      return String(this.payload.seller);
    }
    return seller.code ? `${seller.name} (${seller.code})` : seller.name;
  }

  @Watch("visible", { immediate: true })
  private onVisibleChange(val: boolean) {
    this.dialogVisible = val;
    if (val) {
      this.resetForm();
      this.$nextTick(() => {
        const form = this.$refs.dataForm as Form;
        form && form.clearValidate();
      });
    }
  }

  @Watch("markInput")
  private onMarkInputChange(val: string[]) {
    this.payload.shippingMark = (val || []).join("；");
  }

  private resetForm() {
    this.payload = new WriteShippingOrderInput();
    this.payload.company = "HQ";
    this.markInput = [];
    this.markLetters = [];
    this.shippingCodePrefix = "";
    this.shippingCodeSuffix = "";
    this.showSelectCustomer = false;
    this.showMarkEdit = false;
    this.submitting = false;
    this.previewLoading = false;
  }

  private handleCancel() {
    this.$emit("update:visible", false);
  }

  private clearCustomer() {
    const company = this.payload.company || "HQ";
    this.payload = new WriteShippingOrderInput();
    this.payload.company = company;
    this.markInput = [];
    this.markLetters = [];
    this.shippingCodePrefix = "";
    this.shippingCodeSuffix = "";
    this.$nextTick(() => {
      const form = this.$refs.dataForm as Form;
      form && form.clearValidate();
    });
  }

  private openMarkEdit() {
    if (!this.payload.id) {
      this.$message.warning("请先选择客人");
      return;
    }
    this.showMarkEdit = true;
  }

  private async onCustomerConfirm(customer: SelectedCustomer) {
    if (!customer || !customer.code) return;
    await this.fillCustomerInfo(customer.code);
  }

  private async fillCustomerInfo(customerCode: string) {
    try {
      const company = this.payload.company || "HQ";
      const data = (await getCustomerAllInfo(
        customerCode
      )) as WriteShippingOrderInput;
      data.company = company;
      data.shippingMark = this.payload.shippingMark;
      this.payload = {
        ...new WriteShippingOrderInput(),
        ...data,
        company,
        customerId: data.id,
      };
      const markData: any = await queryMarkLetter([], this.payload.id);
      this.markLetters = (markData || []).map((item) => new Mark(item, false));
      await this.fetchShippingCodePreview();
    } catch (e) {
      console.error(e);
    }
  }

  private async onCompanyChange() {
    if (this.payload.id) {
      await this.fetchShippingCodePreview();
    }
  }

  private async fetchShippingCodePreview() {
    if (!this.payload.company || !this.payload.id) {
      this.shippingCodePrefix = "";
      this.shippingCodeSuffix = "";
      return;
    }
    this.previewLoading = true;
    try {
      const res: any = await shippingOrderCodePreview({
        company: this.payload.company,
        customerId: this.payload.id,
      });
      this.shippingCodePrefix = res?.prefix || "";
      this.shippingCodeSuffix = String(res?.suffix || "")
        .replace(/\D/g, "")
        .slice(0, 4)
        .padStart(4, "0");
      this.syncShippingCode();
    } catch (e) {
      this.shippingCodePrefix = "";
      this.shippingCodeSuffix = "";
      this.payload.shippingCode = null;
    } finally {
      this.previewLoading = false;
    }
  }

  private syncShippingCode() {
    if (!this.shippingCodePrefix || !this.shippingCodeSuffix) {
      this.payload.shippingCode = null;
      return;
    }
    this.payload.shippingCode = `${this.shippingCodePrefix}${this.shippingCodeSuffix}`;
  }

  private onSuffixInput(value: string) {
    const digits = String(value || "")
      .replace(/\D/g, "")
      .slice(0, 4);
    this.shippingCodeSuffix = digits;
    this.syncShippingCode();
  }

  private handlerSelected(selected: string, type: string) {
    this.payload[type] = selected;
  }

  private handleEditMarkSuccess(marks: Mark[]) {
    this.markInput = marks.map((item) => item.value);
    this.showMarkEdit = false;
  }

  private buildCreateRequest() {
    const p = this.payload;
    return {
      company: p.company,
      customerId: p.customerId || p.id,
      shippingOrderCode: p.shippingCode,
      fivePiecesSetInfo: {
        fivePiecesSetCompanyName: p.fivePiecesSetCompanyName,
        fivePiecesSetCompanyAddress: p.fivePiecesSetCompanyAddress,
        fivePiecesSetCompanyOtherInfo: p.fivePiecesSetCompanyOtherInfo,
      },
      paymentInfo: {
        transportation: p.transportation,
        internationalTradeTerms: p.internationalTradeTerms,
        payment: p.payment,
      },
      countryAndPortsInfo: {
        nation: p.nation,
        departures: p.departures,
        tradeCountry: p.tradeCountry,
        departure: p.departure,
        destinationCountry: p.destinationCountry,
        deliveryCountry: p.deliveryCountry,
        portDischarge: p.portDischarge,
      },
      blInfo: {
        notifier: p.notifier,
        notifierAddress: p.notifierAddress,
        blNotifyPersonInfo: p.blNotifyPersonInfo,
        name: p.name,
        address: p.address,
        tel: p.tel,
      },
      customerOtherInfo: {
        shippingMark: p.shippingMark,
        paymentDetails: p.paymentDetails,
        ladingType: p.ladingType,
      },
    };
  }

  private handleSubmit() {
    if (!this.payload.code || !this.payload.id) {
      this.$message.warning("请先选择客人");
      return;
    }
    if (!/^\d{4}$/.test(this.shippingCodeSuffix)) {
      this.$message.warning("订舱号后四位必须为四位数字");
      return;
    }
    this.syncShippingCode();
    this.payload.shippingMark = this.markInput.join("；");
    (this.$refs.dataForm as Form).validate(async (valid) => {
      if (!valid) return;
      this.submitting = true;
      try {
        await creatShipping(this.buildCreateRequest());
        this.$notify({
          title: "成功",
          message: "创建成功",
          type: "success",
          duration: 2000,
        });
        this.$emit("success");
        this.handleCancel();
      } catch (e) {
        console.error(e);
      } finally {
        this.submitting = false;
      }
    });
  }
}

class WriteShippingOrderInput {
  id: string;
  code: string;
  name = "";
  abbreviation = "";
  address = "";
  tel = "";
  nation = "";
  departures = "";
  tradeCountry = "";
  departure = "";
  transportation = "";
  destinationCountry = "";
  deliveryCountry = "";
  portDischarge = "";
  internationalTradeTerms = "";
  payment = "";
  paymentDetails = "";
  ladingType = "";
  notifier = "";
  notifierAddress = "";
  blNotifyPersonInfo = "";
  seller: string;
  customerId: string;
  startDate: Moment;
  billLadingCode = "";
  typeCount: number;
  combinedContainer: boolean;
  fivePiecesSetCompanyName: string;
  fivePiecesSetCompanyAddress: string;
  fivePiecesSetCompanyOtherInfo: string;
  containerTypes: IContainerTypesModel[] = [];
  shippingMark = "";
  shippingCode: string = null;
  company = "HQ";
  fivePiecesSetCompanyNames = [];
  fivePiecesSetCompanyAddresses = [];
  fivePiecesSetCompanyOtherInfos = [];
  blNotifyPersonInfos = [];
  blNotifiers = [];
  blNotifiersAddress = [];
  blCompanyNames = [];
  blCompanyAddresses = [];
  blCompanyInfos = [];
  ivCompanyNames = [];
  ivCompanyAddresses = [];
  ivCompanyInfos = [];
  deliveryCountries = [];
  destinationCountries = [];
  departuresList = [];
  portDischarges = [];
  ladingTypes = [];
  departureList = [];
  transportations = [];
}
